import { useEffect, useRef, useCallback } from "react";
import { useUser } from "@/contexts/UserContext";
import { supabase } from "@/integrations/supabase/client";
import { useLocation } from "react-router-dom";

interface InteractionEvent {
  user_id: string;
  event_type: string;
  entity_type?: string;
  entity_id?: string;
  metadata?: Record<string, any>;
  session_id: string;
  page_path: string;
}

const SESSION_ID = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const BATCH_INTERVAL = 30000; // 30 seconds
const MAX_BATCH_SIZE = 50;

/**
 * Product funnel events (Batch C, Task 6).
 *
 * These go to public.user_interaction_log — NOT PostHog. CLAUDE.md §20 keeps
 * PostHog capture confined to ComprehensiveErrorBoundary + main.tsx so error
 * telemetry stays clean; that rule remains in force. Do not add PostHog capture
 * sites for product analytics.
 *
 * This is the SAME write path as the tracker hook above: same table, same
 * columns, same SESSION_ID. `trackProductEvent` exists only so milestone events
 * can be emitted from services and async handlers that are not React components,
 * and so they flush immediately instead of sitting in the 30s batch (a batched
 * signup_completed would be lost to the redirect that follows it).
 */
export const PRODUCT_EVENTS = {
  SIGNUP_STARTED: "signup_started",
  SIGNUP_COMPLETED: "signup_completed",
  PERSONA_SELECTED: "persona_selected",
  VERIFICATION_STARTED: "verification_started",
  VERIFICATION_DOCUMENT_UPLOADED: "verification_document_uploaded",
  VERIFICATION_SUBMITTED: "verification_submitted",
  JOB_POST_STARTED: "job_post_started",
  JOB_POSTED: "job_posted",
  APPLICATION_STARTED: "application_started",
  APPLICATION_SUBMITTED: "application_submitted",
  SUBSCRIPTION_VIEWED: "subscription_viewed",
  SUBSCRIPTION_STARTED: "subscription_started",
  CALL_INITIATED: "call_initiated",
  CALL_CONNECTED: "call_connected",
  /**
   * Emitted by trackSearch() below. computePlatformInsights (ai-knowledge-engine)
   * aggregates `event_type = 'search'` rows by `metadata.query` into the
   * `trending_searches` platform insight — that panel read empty from March to
   * August 2026 because nothing in the app ever wrote this event type.
   */
  SEARCH: "search",
} as const;

export type ProductEvent = (typeof PRODUCT_EVENTS)[keyof typeof PRODUCT_EVENTS];

/**
 * Keys that must never reach user_interaction_log.metadata.
 * The table is admin-readable, so anything landing here is effectively a
 * secondary copy of the data. Identity and money stay out.
 */
const FORBIDDEN_METADATA_KEYS = [
  "cnic", "nic", "ntn", "phone", "mobile", "email", "auth_email",
  "password", "token", "salary", "pay_rate", "amount", "monthly_price",
  "full_name", "name", "address",
];

type MetadataPrimitive = string | number | boolean | null;

const sanitizeMetadata = (
  metadata?: Record<string, unknown>
): Record<string, MetadataPrimitive> => {
  if (!metadata) return {};
  const clean: Record<string, MetadataPrimitive> = {};
  for (const [key, value] of Object.entries(metadata)) {
    const lowered = key.toLowerCase();
    if (FORBIDDEN_METADATA_KEYS.some((bad) => lowered.includes(bad))) continue;
    // Only primitives — no nested objects that could smuggle PII through.
    if (value === null || ["string", "number", "boolean"].includes(typeof value)) {
      clean[key] = value as MetadataPrimitive;
    }
  }
  return clean;
};

interface ProductEventOptions {
  entityType?: string;
  entityId?: string;
  metadata?: Record<string, unknown>;
  /** Pass when the caller already knows the id (e.g. straight after signUp). */
  userId?: string;
}

/**
 * Fire-and-forget product event. Never throws, never rejects, never blocks the
 * user action that triggered it. Safe to call without `await`.
 *
 * NOTE ON PRE-AUTH EVENTS: user_interaction_log.user_id is NOT NULL and the
 * INSERT policy is `auth.uid() = user_id`, so a genuinely anonymous event cannot
 * be stored. signup_started is therefore emitted once the account exists,
 * carrying the real client-side start time in metadata so time-to-complete stays
 * measurable. Signup ABANDONMENT is not measurable in this table by design.
 */
export async function trackProductEvent(
  eventType: ProductEvent,
  options: ProductEventOptions = {}
): Promise<void> {
  try {
    let userId = options.userId;
    if (!userId) {
      // Local read of the cached session — no network round-trip.
      const { data } = await supabase.auth.getSession();
      userId = data.session?.user?.id;
    }
    if (!userId) return; // Not authenticated yet — nothing we may write.

    await supabase.from("user_interaction_log").insert({
      user_id: userId,
      event_type: eventType,
      entity_type: options.entityType ?? null,
      entity_id: options.entityId ?? null,
      metadata: sanitizeMetadata(options.metadata),
      session_id: SESSION_ID,
      page_path: typeof window !== "undefined" ? window.location.pathname : null,
    });
  } catch {
    // Analytics must never break a user action.
  }
}

/** Queries that look like identity/contact data are never logged. */
const SENSITIVE_QUERY_PATTERNS = [
  /\d{5}-?\d{7}-?\d/,        // CNIC
  /(?:\+?92|0)3\d{2}-?\d{7}/, // PK mobile
  /@/,                        // email-ish
];

const MAX_QUERY_LENGTH = 100;
const SEARCH_SETTLE_MS = 1200;

let searchSettleTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Record a search the user actually performed.
 *
 * Trailing-debounced by SEARCH_SETTLE_MS so a term typed one character at a
 * time produces ONE row for the settled query rather than a row per prefix.
 * Writes through trackProductEvent — same table, same session id, same
 * sanitiser as every other product event. `metadata.query` is the exact key
 * computePlatformInsights aggregates on; do not rename it.
 *
 * Fire-and-forget. Never awaited, never throws.
 */
export function trackSearch(
  query: string,
  options: { scope?: string; resultCount?: number } = {}
): void {
  const trimmed = (query || "").trim();
  if (trimmed.length < 2) return;
  if (SENSITIVE_QUERY_PATTERNS.some((re) => re.test(trimmed))) return;

  const normalized = trimmed.slice(0, MAX_QUERY_LENGTH).toLowerCase();

  if (searchSettleTimer) clearTimeout(searchSettleTimer);
  searchSettleTimer = setTimeout(() => {
    searchSettleTimer = undefined;
    void trackProductEvent(PRODUCT_EVENTS.SEARCH, {
      entityType: options.scope ?? "search",
      metadata: {
        query: normalized,
        scope: options.scope ?? "global",
        ...(typeof options.resultCount === "number" ? { result_count: options.resultCount } : {}),
      },
    });
  }, SEARCH_SETTLE_MS);
}

/**
 * Lightweight interaction tracker that batches events and sends every 30s.
 * Tracks: page views, clicks, searches, scroll depth, time-on-page.
 */
export const useInteractionTracker = () => {
  const { user } = useUser();
  const location = useLocation();
  const batchRef = useRef<InteractionEvent[]>([]);
  const pageEnteredRef = useRef<number>(Date.now());
  const lastPageRef = useRef<string>("");
  const flushTimerRef = useRef<ReturnType<typeof setInterval>>();

  const addEvent = useCallback((
    eventType: string,
    entityType?: string,
    entityId?: string,
    metadata?: Record<string, any>
  ) => {
    if (!user?.id) return;
    
    batchRef.current.push({
      user_id: user.id,
      event_type: eventType,
      entity_type: entityType,
      entity_id: entityId,
      metadata,
      session_id: SESSION_ID,
      page_path: location.pathname,
    });

    // Auto-flush if batch gets large
    if (batchRef.current.length >= MAX_BATCH_SIZE) {
      flushBatch();
    }
  }, [user?.id, location.pathname]);

  const flushBatch = useCallback(async () => {
    if (batchRef.current.length === 0 || !user?.id) return;

    // Events are queued against the React-context `user.id` but flushed up to
    // BATCH_INTERVAL later, against whatever auth.uid() is at that moment. The
    // INSERT policy on user_interaction_log is `auth.uid() = user_id`
    // (authenticated only), so if the session changed in between — which is
    // exactly what the OAuth account-swap paths do, signOut({scope:'local'})
    // then restore — every row in the batch violates RLS and Postgres rejects
    // the WHOLE insert. That surfaced as recurring
    //   "new row violates row-level security policy for table
    //    user_interaction_log"
    // in the Postgres log, and the old code discarded the entire batch to a
    // console.warn, so events belonging to the still-valid current user were
    // lost alongside the stale ones.
    //
    // Reconcile against the live session before writing: keep what is
    // writable, drop what provably is not.
    let liveUserId: string | null = null;
    try {
      const { data } = await supabase.auth.getSession();
      liveUserId = data.session?.user?.id ?? null;
    } catch {
      /* treated as "no live session" below */
    }

    if (!liveUserId) {
      // Mid-swap or signed out. Do NOT drop the queue — the session usually
      // reappears within a tick or two; try again on the next flush.
      return;
    }

    // Rows for a different uid can never be written under this policy. Drop
    // them deliberately rather than letting them poison the batch.
    const writable = batchRef.current.filter((e) => e.user_id === liveUserId);
    batchRef.current = [];
    if (writable.length === 0) return;

    const events = writable;

    try {
      const { error } = await supabase.from("user_interaction_log").insert(
        events.map(e => ({
          user_id: e.user_id,
          event_type: e.event_type,
          entity_type: e.entity_type || null,
          entity_id: e.entity_id || null,
          metadata: e.metadata || {},
          session_id: e.session_id,
          page_path: e.page_path,
        }))
      );
      if (error) console.warn("Interaction log flush failed:", error.message);
    } catch (e) {
      // Never block the app for tracking failures
      console.warn("Interaction tracking error:", e);
    }
  }, [user?.id]);

  // Track page navigation
  useEffect(() => {
    if (!user?.id) return;
    
    const currentPath = location.pathname;
    
    // Log time on previous page
    if (lastPageRef.current && lastPageRef.current !== currentPath) {
      const timeSpent = Math.round((Date.now() - pageEnteredRef.current) / 1000);
      addEvent("time_on_page", "page", lastPageRef.current, { seconds: timeSpent });
    }

    // Log new page navigation
    addEvent("navigate", "page", currentPath);
    lastPageRef.current = currentPath;
    pageEnteredRef.current = Date.now();
  }, [location.pathname, user?.id, addEvent]);

  // Set up periodic flush
  useEffect(() => {
    flushTimerRef.current = setInterval(flushBatch, BATCH_INTERVAL);
    
    // Flush on page unload
    const handleBeforeUnload = () => {
      if (batchRef.current.length > 0 && user?.id) {
        // Use sendBeacon for reliability on page close
        const events = batchRef.current.map(e => ({
          user_id: e.user_id,
          event_type: e.event_type,
          entity_type: e.entity_type || null,
          entity_id: e.entity_id || null,
          metadata: e.metadata || {},
          session_id: e.session_id,
          page_path: e.page_path,
        }));
        
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-knowledge-engine`;
        const payload = JSON.stringify({
          action: "batch_interactions",
          data: { events },
        });
        // Chrome throws SecurityError for non-CORS-safelisted Blob types
        // (crbug.com/490015); telemetry must never throw from beforeunload.
        try {
          navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
        } catch {
          // best-effort: the unmount flushBatch() still covers the queue
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    
    return () => {
      clearInterval(flushTimerRef.current);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      flushBatch(); // Final flush on unmount
    };
  }, [flushBatch, user?.id]);

  // Track scroll depth (debounced)
  useEffect(() => {
    if (!user?.id) return;
    
    let maxScroll = 0;
    let scrollTimer: ReturnType<typeof setTimeout>;
    
    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      if (scrollPercent > maxScroll) maxScroll = scrollPercent;
      
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        if (maxScroll > 25) { // Only log meaningful scrolls
          addEvent("scroll", "page", location.pathname, { depth_percent: maxScroll });
          maxScroll = 0;
        }
      }, 2000);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimer);
    };
  }, [user?.id, location.pathname, addEvent]);

  // Public API for explicit tracking from components
  const trackClick = useCallback((elementId: string, metadata?: Record<string, any>) => {
    addEvent("click", "button", elementId, metadata);
  }, [addEvent]);

  const trackSearch = useCallback((query: string, metadata?: Record<string, any>) => {
    addEvent("search", "search_query", query, { query, ...metadata });
  }, [addEvent]);

  const trackJobView = useCallback((jobId: string, metadata?: Record<string, any>) => {
    addEvent("view", "job", jobId, metadata);
  }, [addEvent]);

  const trackApply = useCallback((jobId: string, metadata?: Record<string, any>) => {
    addEvent("apply", "job", jobId, metadata);
  }, [addEvent]);

  return { trackClick, trackSearch, trackJobView, trackApply, addEvent };
};
